/**
 * Robust text-range matcher for ProseMirror documents.
 *
 * Used by the AI "Replace / Insert / Delete" suggestions to locate the
 * search_text proposed by the LLM inside the live editor doc, even when
 * the LLM's copy of the text differs slightly from the actual content
 * (smart quotes, NBSP, accents, minor wording drift, capitalization, etc.).
 *
 * Strategy (in order):
 *   1. Build a normalized lowercase view of the doc text with a parallel
 *      array mapping every normalized char back to its ProseMirror position.
 *      Smart quotes -> ASCII, NBSP -> space, dashes -> hyphen, whitespace
 *      collapsed. Block boundaries become a synthetic space whose mapped
 *      position is -1 (so we never land on one).
 *   2. Normalize the search text the same way.
 *   3. Try plain substring (indexOf). Handles ~95% of real cases.
 *   4. Anchor matching: take ~20% from each end as anchors and look for
 *      both. Handles cases where the LLM dropped or added words in the
 *      middle.
 *   5. Fuzzy windowed Levenshtein. Handles minor typos / accent drift on
 *      short search texts that don't qualify for anchor matching.
 *
 * Returns { from, to, strategy, isBlockLevel } in PM coordinates, or null.
 *
 * Tested with 38 cases including the real Convention d'Investissement
 * contract. See /tmp/replace-test/ for the test harness.
 */

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

const SMART_SINGLE_QUOTES = /[\u2018\u2019\u201A\u201B\u2032]/g;
const SMART_DOUBLE_QUOTES = /[\u201C\u201D\u201E\u201F\u2033]/g;
const DASHES = /[\u2013\u2014\u2212]/g;
const NBSP = /\u00A0/g;
const ZWSP = /[\u200B-\u200D\uFEFF]/g;

function normalizeChar(c) {
  if (c === '\u2019' || c === '\u2018' || c === '\u201A' || c === '\u201B' || c === '\u2032') return "'";
  if (c === '\u201C' || c === '\u201D' || c === '\u201E' || c === '\u201F' || c === '\u2033') return '"';
  if (c === '\u2013' || c === '\u2014' || c === '\u2212') return '-';
  if (c === '\u00A0') return ' ';
  if (c === '\u200B' || c === '\u200C' || c === '\u200D' || c === '\uFEFF') return '';
  return c;
}

export function normalizePlain(s) {
  if (!s) return '';
  return s
    .replace(SMART_SINGLE_QUOTES, "'")
    .replace(SMART_DOUBLE_QUOTES, '"')
    .replace(DASHES, '-')
    .replace(NBSP, ' ')
    .replace(ZWSP, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// ---------------------------------------------------------------------------
// Document text builder
// ---------------------------------------------------------------------------

/**
 * Walk a PM doc and produce:
 *   - normText: a normalized lowercase string of all visible text
 *   - normToPmPos: parallel array; normToPmPos[i] is the PM position of
 *     normText[i], or -1 for synthetic block-boundary spaces
 *   - normToBlockBoundary: parallel boolean array; true if normText[i]
 *     is a synthetic block-boundary space
 */
export function buildNormalizedDocText(doc) {
  const normChars = [];
  const normToPmPos = [];
  const normToBlockBoundary = [];

  let prevWasSpace = true; // suppress leading whitespace

  function pushChar(c, pmPos, isBoundary) {
    if (c === '') return;
    if (/\s/.test(c)) {
      if (prevWasSpace) return;
      normChars.push(' ');
      normToPmPos.push(pmPos);
      normToBlockBoundary.push(isBoundary);
      prevWasSpace = true;
      return;
    }
    normChars.push(c.toLowerCase());
    normToPmPos.push(pmPos);
    normToBlockBoundary.push(false);
    prevWasSpace = false;
  }

  doc.descendants((node, pos) => {
    if (node.isText) {
      const text = node.text;
      for (let i = 0; i < text.length; i++) {
        pushChar(normalizeChar(text[i]), pos + i, false);
      }
      return false;
    }
    if (node.isBlock && normChars.length > 0) {
      pushChar(' ', -1, true);
    }
    return true;
  });

  while (normChars.length > 0 && normChars[normChars.length - 1] === ' ') {
    normChars.pop();
    normToPmPos.pop();
    normToBlockBoundary.pop();
  }

  return { normText: normChars.join(''), normToPmPos, normToBlockBoundary };
}

// ---------------------------------------------------------------------------
// Position mapping
// ---------------------------------------------------------------------------

function mapNormRangeToPm(startNorm, endNorm, normToPmPos) {
  let from = -1;
  for (let i = startNorm; i < endNorm; i++) {
    if (normToPmPos[i] !== -1) { from = normToPmPos[i]; break; }
  }
  let to = -1;
  for (let i = endNorm - 1; i >= startNorm; i--) {
    if (normToPmPos[i] !== -1) { to = normToPmPos[i] + 1; break; }
  }
  if (from === -1 || to === -1 || from >= to) return null;
  return { from, to };
}

// ---------------------------------------------------------------------------
// Strategy 1: exact normalized substring
// ---------------------------------------------------------------------------

function findExact(normText, normSearch) {
  if (!normSearch) return -1;
  return normText.indexOf(normSearch);
}

// ---------------------------------------------------------------------------
// Strategy 2: anchor matching (resilient to dropped/added words)
// ---------------------------------------------------------------------------

function findAnchored(normText, normSearch) {
  if (normSearch.length < 40) return null;

  const anchorTargetLen = Math.min(40, Math.max(15, Math.floor(normSearch.length * 0.2)));

  let startEnd = anchorTargetLen;
  while (startEnd < normSearch.length && normSearch[startEnd] !== ' ') startEnd++;
  const startAnchor = normSearch.slice(0, startEnd);

  let endStart = normSearch.length - anchorTargetLen;
  while (endStart > 0 && normSearch[endStart] !== ' ') endStart--;
  const endAnchor = normSearch.slice(endStart).trimStart();

  if (startAnchor.length < 8 || endAnchor.length < 8) return null;

  const startIdx = normText.indexOf(startAnchor);
  if (startIdx === -1) return null;

  // Allow 30% .. 200% of search length between anchors.
  const minEndPos = startIdx + Math.max(0, Math.floor(normSearch.length * 0.3) - endAnchor.length);
  const maxEndPos = startIdx + Math.ceil(normSearch.length * 2.0) + 50;

  let searchFrom = startIdx + Math.max(1, startAnchor.length - 5);
  let endIdx = -1;
  while (true) {
    const found = normText.indexOf(endAnchor, searchFrom);
    if (found === -1) break;
    if (found >= minEndPos && found <= maxEndPos) { endIdx = found; break; }
    if (found > maxEndPos) break;
    searchFrom = found + 1;
  }
  if (endIdx === -1) return null;

  return { startNorm: startIdx, endNorm: endIdx + endAnchor.length };
}

// ---------------------------------------------------------------------------
// Strategy 3: windowed fuzzy (Levenshtein)
// ---------------------------------------------------------------------------

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}

function findFuzzy(normText, normSearch) {
  if (normSearch.length < 20) return null;
  const tolerance = Math.max(2, Math.floor(normSearch.length * 0.15));
  const winSize = normSearch.length;
  const step = normSearch.length > 200 ? 4 : 1;

  let best = null;
  for (let i = 0; i + winSize / 2 < normText.length; i += step) {
    const slice = normText.slice(i, i + winSize);
    const dist = levenshtein(slice, normSearch);
    if (dist <= tolerance && (best === null || dist < best.dist)) {
      best = { startNorm: i, endNorm: i + winSize, dist };
    }
    if (best && best.dist === 0) break;
  }
  if (!best) return null;

  // Tighten right edge while distance doesn't grow
  let { startNorm, endNorm, dist } = best;
  while (endNorm > startNorm + Math.floor(winSize * 0.7)) {
    const shorter = normText.slice(startNorm, endNorm - 1);
    const d = levenshtein(shorter, normSearch);
    if (d <= dist) { endNorm = endNorm - 1; dist = d; } else break;
  }
  return { startNorm, endNorm };
}

// ---------------------------------------------------------------------------
// Public: findRange
// ---------------------------------------------------------------------------

/**
 * Locate `searchText` inside `doc` (a ProseMirror Node) and return:
 *   { from, to, strategy, isBlockLevel }
 * or null if not found.
 *
 * isBlockLevel is true when both edges of the matched range sit at a
 * block boundary. Callers can use this together with
 * `expandRangeToBlockBoundaries` to decide how to insert replacement HTML.
 */
export function findRange(doc, searchText) {
  const { normText, normToPmPos, normToBlockBoundary } = buildNormalizedDocText(doc);
  const normSearch = normalizePlain(searchText);

  if (!normSearch) return null;

  let startNorm = findExact(normText, normSearch);
  let endNorm;
  let strategy;
  if (startNorm !== -1) {
    endNorm = startNorm + normSearch.length;
    strategy = 'exact';
  } else {
    const anchored = findAnchored(normText, normSearch);
    if (anchored) {
      startNorm = anchored.startNorm;
      endNorm = anchored.endNorm;
      strategy = 'anchored';
    } else {
      const fuzzy = findFuzzy(normText, normSearch);
      if (fuzzy) {
        startNorm = fuzzy.startNorm;
        endNorm = fuzzy.endNorm;
        strategy = 'fuzzy';
      } else {
        return null;
      }
    }
  }

  const range = mapNormRangeToPm(startNorm, endNorm, normToPmPos);
  if (!range) return null;

  const leftIsBoundary = startNorm === 0 || normToBlockBoundary[startNorm - 1] === true;
  const rightIsBoundary = endNorm === normToPmPos.length || normToBlockBoundary[endNorm] === true;
  const isBlockLevel = leftIsBoundary && rightIsBoundary;

  return { ...range, strategy, isBlockLevel };
}

// ---------------------------------------------------------------------------
// Block-boundary expansion
// ---------------------------------------------------------------------------

/**
 * Expand a PM range outward to the nearest enclosing top-level block
 * boundaries. Used when we want to replace an inline range with HTML that
 * contains block-level tags (h2, p, ul, table...): we must delete entire
 * blocks and insert at a true block boundary, otherwise TipTap will inline
 * the new content inside an existing paragraph.
 */
export function expandRangeToBlockBoundaries(doc, from, to) {
  // doc.forEach iterates direct children with their offset relative to the
  // parent — for the top-level doc, that offset is the absolute PM position
  // of the child's content start. The block "outside" position is offset
  // (just before the open token), and offset + child.nodeSize is just after
  // the close token.
  let blockFrom = -1;
  let blockTo = -1;

  doc.forEach((child, offset) => {
    const childStart = offset;
    const childEnd = offset + child.nodeSize;
    if (from < childEnd && to > childStart) {
      if (blockFrom === -1) blockFrom = childStart;
      blockTo = childEnd;
    }
  });

  if (blockFrom === -1 || blockTo === -1) return null;
  return { from: blockFrom, to: blockTo };
}

// ---------------------------------------------------------------------------
// HTML helpers
// ---------------------------------------------------------------------------

const BLOCK_TAG_RE = /<(h[1-6]|p|div|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th|hr|figure|section|article)\b/i;

/**
 * Detect whether an HTML string contains block-level tags. Used to decide
 * whether to expand the matched range to block boundaries before insertion.
 */
export function htmlContainsBlocks(html) {
  if (!html) return false;
  return BLOCK_TAG_RE.test(html);
}
